import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import React, { useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Checkbox, CheckboxField, CheckboxGroup } from "src/components/base/checkbox";
import { ApiInternal } from "src/api/api_internal";
import { notify } from "src/components/base/toast";
import { UUID } from "src/api/api_common";

/**
 * The properties for {@link AddResolverDialog}
 */
export type AddResolverDialogProps = {
    /** The domain to add the resolver to */
    domainUuid: UUID | undefined;
    /** Whether the dialog is open or not */
    isOpen: boolean;
    /** Callback for closing the dialog */
    onClose: () => void;
    /** Callback for submitting the form */
    onSubmit: () => void;
};

/**
 * A dialog for adding a new resolver to a split dns domain
 */
export default function AddResolverDialog(props: AddResolverDialogProps) {
    const [t] = useTranslation("dialog-add-resolver");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            resolverIp: "",
            description: "",
            disabled: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!props.domainUuid) return;
                const res = await ApiInternal.dns.addResolverSplitDns(props.domainUuid, {
                    ip: value.resolverIp,
                    description: value.description,
                    disabled: value.disabled,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            resolverIp: res.error.ip_invalid
                                ? t("error.invalid-ip")
                                : res.error.ip_already_exists
                                  ? t("error.ip-already-exists")
                                  : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            notify.success(t("toast.saved-successfully"));
            props.onSubmit();
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen]);

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <Form onSubmit={form.handleSubmit}>
                <DialogTitle>{t("heading.add-resolver")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"resolverIp"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.ip")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            maxLength={255}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Input
                                            maxLength={1024}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"disabled"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.disabled")}</Label>
                                        <CheckboxGroup>
                                            <CheckboxField>
                                                <Checkbox
                                                    checked={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e)}
                                                />
                                                <Label>{t("description.disabled")}</Label>
                                            </CheckboxField>
                                        </CheckboxGroup>
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                    <PrimaryButton type={"submit"}>{tg("button.add")}</PrimaryButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
}
